import React, { useState, useEffect, useMemo } from "react";
import { ChevronDown, ChevronUp, Pencil, Plus } from "lucide-react";
import { getSkills } from "@/api/skills";
import SkillsForm from "@/pages/admin/skills/SkillsForm";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function SkillsIndex() {
    const [skills, setSkills] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sortConfig, setSortConfig] = useState({ key: ["category", "name"], direction: "asc" });

    const [modalOpen, setModalOpen] = useState(false);
    const [selectedSkill, setSelectedSkill] = useState(null); // null = new

    useEffect(() => {
        loadSkills();
    }, []);

    const loadSkills = async () => {
        setLoading(true);
        const data = await getSkills();
        if (data) setSkills(data);
        setLoading(false);
    };

    const handleOpenModal = (skill = null) => {
        setSelectedSkill(skill);
        setModalOpen(true);
    };

    const handleSave = () => {
        setModalOpen(false);
        setSelectedSkill(null);
        loadSkills(); // refresh table
    };

    const sortedSkills = useMemo(() => {
        const sorted = [...skills];
        const { key, direction } = sortConfig;

        const getValue = (item, key) => {
            if (key === "category") return item.skillsCategory?.categoryName?.toLowerCase() || "";
            return item[key]?.toString().toLowerCase() || "";
        };

        sorted.sort((a, b) => {
            if (Array.isArray(key)) {
                for (const k of key) {
                    const valA = getValue(a, k);
                    const valB = getValue(b, k);
                    if (valA < valB) return direction === "asc" ? -1 : 1;
                    if (valA > valB) return direction === "asc" ? 1 : -1;
                }
                return 0;
            } else {
                const valA = getValue(a, key);
                const valB = getValue(b, key);
                if (valA < valB) return direction === "asc" ? -1 : 1;
                if (valA > valB) return direction === "asc" ? 1 : -1;
                return 0;
            }
        });

        return sorted;
    }, [skills, sortConfig]);

    const handleSort = (column) => {
        setSortConfig((prev) => {
            const isSame = Array.isArray(prev.key)
                ? prev.key[0] === column
                : prev.key === column;

            return {
                key: column,
                direction: isSame && prev.direction === "asc" ? "desc" : "asc",
            };
        });
    };

    const renderSortIcon = (column) => {
        const isActive = Array.isArray(sortConfig.key)
            ? sortConfig.key.includes(column)
            : sortConfig.key === column;

        if (!isActive) return null;

        return sortConfig.direction === "asc" ? (
            <ChevronUp className="ml-1 h-4 w-4 inline-block" />
        ) : (
            <ChevronDown className="ml-1 h-4 w-4 inline-block" />
        );
    };

    return (
        <div className="bg-white shadow rounded-xl p-6">
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-semibold text-[#5D1F42]">Job Titles & Pricing</h2>
                <button
                    onClick={() => handleOpenModal(null)}
                    className="flex items-center gap-2 bg-[#5D1F42] text-white text-sm px-4 py-2 rounded-lg hover:bg-[#712a55] transition"
                >
                    <Plus className="w-4 h-4" />
                    Add New Job Title
                </button>
            </div>

            {loading ? (
                <p className="text-gray-500">Loading job titles...</p>
            ) : (
                <div className="overflow-auto">
                    <table className="min-w-full border border-gray-200 text-sm rounded-xl overflow-hidden">
                        <thead className="bg-gray-50 text-left text-gray-700 uppercase text-xs tracking-wider">
                        <tr>
                            {[
                                { label: "Category", key: "category" },
                                { label: "Job Title", key: "name" },
                                { label: "Starting Price", key: "priceStart" },
                                { label: "Top Price", key: "priceEnd" },
                                { label: "Price Unit", key: "pricePerUnit" },
                                { label: "Pay Rate (Weekly)", key: "payRate" },
                                { label: "", key: "actions" },
                            ].map(({ label, key }) => (
                                <th
                                    key={key}
                                    onClick={key !== "actions" ? () => handleSort(key) : undefined}
                                    className={`px-4 py-3 border-b ${
                                        key !== "actions" ? "cursor-pointer select-none hover:text-[#5D1F42]" : ""
                                    }`}
                                >
                                    <div className="flex items-center">
                                        {label}
                                        {key !== "actions" && renderSortIcon(key)}
                                    </div>
                                </th>
                            ))}
                        </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                        {sortedSkills.map((skill) => (
                            <tr key={skill.id} className="hover:bg-gray-50 transition">
                                <td className="px-4 py-2">{skill.skillsCategory?.categoryName || "-"}</td>
                                <td className="px-4 py-2">{skill.name}</td>
                                <td className="px-4 py-2">
                                    {skill.priceStart !== null ? `£${skill.priceStart}` : "—"}
                                </td>
                                <td className="px-4 py-2">
                                    {skill.priceEnd !== null ? `£${skill.priceEnd}` : "—"}
                                </td>
                                <td className="px-4 py-2 capitalize">{skill.pricePerUnit || "—"}</td>
                                <td className="px-4 py-2 font-semibold">
                                    {skill.payRate !== null ? `£${skill.payRate}` : "—"}
                                </td>
                                <td className="px-4 py-2 text-right">
                                    <button
                                        onClick={() => handleOpenModal(skill)}
                                        className="text-sm text-[#5D1F42] hover:underline inline-flex items-center gap-1"
                                    >
                                        <Pencil className="w-4 h-4" />
                                        Edit
                                    </button>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            )}

            <Dialog open={modalOpen} onOpenChange={setModalOpen}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>{selectedSkill ? "Edit Job Title" : "Add New Job Title"}</DialogTitle>
                    </DialogHeader>
                    <SkillsForm skill={selectedSkill} onSave={handleSave} />
                </DialogContent>
            </Dialog>
        </div>
    );
}
